import * as _ from 'underscore';

import { SiteUrls } from '@tableau/shared-urls';

import { FeatureFlags } from '../../ts/FeatureFlags';
import { QueryParameterHelper } from '../../ts/helpers/QueryParameterHelper';
import { FilterFieldNames } from '../../ts/ServerApi/types/IFilterClause';
import { ServiceLocator } from '../ServiceLocator';
import { SiteSpaceContentType } from '../state/SiteSpaceContentTypeState';
import { DatasourceSubspaceName, SubspaceName, WorkbookSubspaceName } from '../state/SpaceInfo';
import { UrlParamKeys } from '../UrlParamKeys';
import { DatasourceSpaceConfigHelper } from './SpaceConfig/DatasourceSpaceConfigHelper';
import { WorkbookSpaceConfigHelper } from './SpaceConfig/WorkbookSpaceConfigHelper';

// Return the query parameters that are in the hash (i.e. from Angular), as an object
export function getQueryParameters(): any {
  const locationService = ServiceLocator.get<ng.ILocationService>('$location');
  return locationService.search();
}

// Add the current query parameters that are in the hash to the given url
export function addQuery(url: string, omitOrderQueryParam: boolean = false): string {
  let params = getQueryParameters();
  if (omitOrderQueryParam) {
    params = _.omit(params, UrlParamKeys.Order);
  }
  return QueryParameterHelper.urlWithParams(url, params);
}

// Return the top level url for the given content type.
export function getSiteUrlForContentType(contentType: SiteSpaceContentType, urls: SiteUrls) {
  switch (contentType) {
    case 'dataconnection':
      throw new Error('invalid site-level content type: dataconnection');
    case 'datasource':
      return urls.datasourcesUrl();
    case 'dataRole':
      return urls.dataRolesUrl();
    case 'explore':
      return urls.exploreUrl();
    case 'flow':
      return urls.flowsUrl();
    case 'project':
      return urls.projectsUrl();
    case 'view':
      return urls.viewsUrl();
    case 'workbook':
      return urls.workbooksUrl();
  }
}

// TODO: This subclass should be removed.
// The remaining methods should either be moved to SiteUrls
// or to separate helpers. We should do that work when removing the mixedContentEnabled feature flag.

export class SpacesSiteUrls extends SiteUrls {

  constructor(config: { siteUrlName: string, queryString?: string }) {
    super({
      siteUrlName: config.siteUrlName,
      // Many existing call sites expect queryString to be filled in automatically
      queryString: config.queryString != null ? config.queryString : window.location.search,
    });
  }

  //
  // URLs go below here
  //

  public exploreSiteDataconnectionsUrl(): string {
    return this.datasourcesUrl() + '?' + FilterFieldNames.contentType + '=' + 'dataconnection';
  }

  public exploreDataConnectionUrl(dataConnectionId: string) {
    // This URL navigates to the data source by its Unified Datasource (connection) ID
      return this.datasourceDataConnectionUrl(dataConnectionId);
  }

  public exploreWorkbookUrl(workbookId: string, subspaceType?: WorkbookSubspaceName) {
    const suffix = WorkbookSpaceConfigHelper.urlSuffixFromSubspaceName(subspaceType);
    const url = this.workbookUrl(workbookId);
    if (suffix) {
      return url + '/' + suffix;
    } else {
      return url;
    }
  }

  public exploreWorkbookDataconnectionsUrl(workbookId: string) {
    const url = this.workbookDatasourcesUrl(workbookId);
    return url + '?' + FilterFieldNames.contentType + '=' + 'dataconnection';
  }

  public userDataconnectionsUrl(username: string, domainName: string) {
    const url = this.userDatasourcesUrl(username, domainName);
    return url + '?' + FilterFieldNames.contentType + '=' + 'dataconnection';
  }

  public exploreDatasourceUrlByTab(datasourceId: string, subspaceType?: DatasourceSubspaceName) {
    const suffix = DatasourceSpaceConfigHelper.urlSuffixFromSubspaceName(subspaceType);
    const url = this.datasourceUrl(datasourceId);
    if (suffix) {
      return url + '/' + suffix;
    } else {
      return url;
    }
  }

  public exploreMetricsUrl(subspace?: SubspaceName): string {
    switch(subspace){
      case 'favorites':
        return this.metricsFavoritesUrl();
      case 'all':
        return this.metricsAllUrl();
      case 'owned':
        return this.metricsOwnedUrl();
      default:
        return this.metricsUrl();
    }
  }

  public contentRootUrl(): string {
    return FeatureFlags.homePage ? this.homeUrl() : this.exploreUrl();
  }

  public contentRootPath(): string {
    return FeatureFlags.homePage ? this.homePath() : this.explorePath();
  }
}
